/**
 * PanRecognizer — continuous position tracking.
 *
 * Reports onPanMove(dx, dy) on every pointermove.
 * Reports onPanEnd(velocityX, velocityY) on pointerup with windowed velocity.
 */

const DEFAULTS = {
    axis: 'horizontal',
};

export class PanRecognizer {
    constructor(config, callbacks) {
        this.config = { ...DEFAULTS, ...config };
        this.callbacks = callbacks;
        this._active = false;
        this._startState = null;
    }

    start(pointerState) {
        this._active = true;
        this._startState = {
            startX: pointerState.startX,
            startY: pointerState.startY,
        };
    }

    move(pointerState, axisLock) {
        if (!this._active || !this._startState) return;

        // If axis lock doesn't match, don't report pan
        if (axisLock && axisLock !== this.config.axis && this.config.axis !== 'both') {
            return;
        }

        const dx = pointerState.currentX - this._startState.startX;
        const dy = pointerState.currentY - this._startState.startY;

        this.callbacks.onPanMove?.(dx, dy);
    }

    end(pointerState, axisLock) {
        if (!this._active) {
            this._reset();
            return;
        }

        // If axis lock doesn't match, cancel silently
        if (axisLock && axisLock !== this.config.axis && this.config.axis !== 'both') {
            this._reset();
            return;
        }

        const velocity = this._calculateVelocity(pointerState.history);
        this.callbacks.onPanEnd?.(velocity.x, velocity.y);
        this._reset();
    }

    pause() {
        this._active = false;
    }

    resume(pointerState, bounds) {
        // After pinch, restart pan from current position
        this._active = true;
        this._startState = {
            startX: pointerState.currentX,
            startY: pointerState.currentY,
        };
    }

    cancel() {
        if (this._active) {
            this.callbacks.onPanEnd?.(0, 0);
        }
        this._reset();
    }

    /**
     * Calculate velocity from windowed history (last 5 events).
     * Returns { x, y } in px/ms.
     */
    _calculateVelocity(history) {
        if (history.length < 2) return { x: 0, y: 0 };

        const recent = history.slice(-5);
        const first = recent[0];
        const last = recent[recent.length - 1];
        const dt = last.t - first.t;

        if (dt <= 0) return { x: 0, y: 0 };

        return {
            x: (last.x - first.x) / dt,
            y: (last.y - first.y) / dt,
        };
    }

    _reset() {
        this._active = false;
        this._startState = null;
    }

    destroy() {
        this._reset();
    }
}
